/**
 * usePortfolioPptxExport (TASK-407)
 *
 * Shared hook used by CompositionTab, ClientLevelView, and PerformanceTab to
 * trigger an LLM-generated portfolio PPTX export. Centralizes:
 *   - loading state during generation
 *   - error toasts (including MultiplePortfoliosError → picker prompt)
 *   - download trigger via the presigned URL returned by the backend
 *
 * Caller passes `clientId` (required) and optionally `portfolioId` /
 * `sessionId` for meeting context. The returned `runExport(option, language)`
 * handler maps ExportDropdown's option to the backend `view` parameter and
 * kicks off the download on success.
 *
 * TASK-877: "on success" now means *a file exists*. The endpoint generates
 * synchronously and slowly, so a response can arrive with no deck attached —
 * `pending`, `rendering`, or a 202 saying another generation holds the lock. Those
 * are waited on via `waitForExport`, and only a `pptx_url` produces a success
 * message. The success toast used to fire on all of them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  isGenerationInFlight,
  isTerminalExportStatus,
  MultiplePortfoliosError,
  waitForExport,
  type ExportResponse,
  type GenerationInFlightResponse,
  type PortfolioExportOptions,
} from '../../../services/api/exportApi';
import { portfolioApiService } from '../../../services/api/portfolioApiService';
import type { ExportLanguage, ExportOption } from '../atoms/ExportDropdown';

interface UsePortfolioPptxExportArgs {
  clientId?: string;
  portfolioId?: string;
  sessionId?: string;
  clientName?: string;
  /** Called after a successful export — caller can route to download, refresh, etc. */
  onExported?: (response: ExportResponse) => void;
  /** Called when CSV is requested — caller does the local CSV emission. */
  onCsv?: () => void;
}

interface UsePortfolioPptxExport {
  loading: boolean;
  error: string | null;
  runExport: (option: ExportOption, language: ExportLanguage) => Promise<void>;
}

/** How long to keep polling `/status` before saying "still generating". */
const POLL_BUDGET_MS = 180_000;

function triggerDownload(href: string) {
  // Presigned URL — let the browser handle the redirect.
  window.open(href, '_blank', 'noopener,noreferrer');
}

/**
 * Did the request give up waiting, rather than the server refuse?
 *
 * TASK-877: the endpoint generates synchronously and a measured run took 82s. That fits
 * inside `httpClientLongRunning`'s 120s, but a slower book does not — and axios rejecting
 * at the deadline is NOT the server reporting a failure. Saying "Export failed" there
 * announces an outcome nobody has observed: the generation is still running and will
 * very likely produce a deck. Distinguishing the two is the whole point of this ticket.
 */
function isClientSideTimeout(err: unknown): boolean {
  const e = err as { code?: string; name?: string; message?: string };
  return (
    e?.code === 'ECONNABORTED' ||
    e?.code === 'ETIMEDOUT' ||
    e?.name === 'AbortError' ||
    e?.name === 'CanceledError' ||
    /timeout/i.test(e?.message ?? '')
  );
}

/**
 * The most specific true sentence available about a failed export.
 *
 * TASK-898: the deck generator calls an LLM, and when the provider refused on billing the
 * chain was `402 -> unhandled APIStatusError -> 500 -> "Request failed with status code 500"`.
 * That is indistinguishable from a bug in the advisor's data, and a client meeting gets
 * scheduled around a deck that does not exist.
 *
 * The backend now returns `{error, message, retryable}` for the cases it can name. Preferring
 * that detail is the whole fix on this side: the words the advisor reads come from the layer
 * that knows what happened.
 */
export function exportFailureMessage(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const d = detail as { message?: string; provider_message?: string; retryable?: boolean };
    if (d.message) {
      // The provider's own words go in the toast too when we have them — an operator reading
      // "you requested up to 16000 tokens, but can only afford 1921" knows what to do.
      return d.provider_message ? `${d.message} (${d.provider_message})` : d.message;
    }
  }
  if (typeof detail === 'string' && detail) return `Export failed: ${detail}`;
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status) return `Export failed (HTTP ${status}). Nothing was produced.`;
  return `Export failed: ${(err as Error)?.message || 'unknown error'}`;
}

export function usePortfolioPptxExport({
  clientId,
  portfolioId,
  sessionId,
  clientName,
  onExported,
  onCsv,
}: UsePortfolioPptxExportArgs): UsePortfolioPptxExport {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TASK-877 (review): the poll must stop when the advisor leaves.
  //
  // `waitForExport` runs for up to `POLL_BUDGET_MS`, and without this it kept issuing
  // `/status` GETs after the component unmounted — then called `toast` and `setState` on
  // a surface nobody is looking at, while `loading` held `ExportDropdown` disabled the
  // whole time. The budget is also cut from the 5 minutes of the first cut: the measured
  // generation is 82s and the gateway now caps the POST at 180s, so a wait longer than
  // that is waiting for something the stack has already given up on.
  const abortRef = useRef<AbortController | null>(null);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const runExport = useCallback(
    async (option: ExportOption, language: ExportLanguage) => {
      if (option === 'csv') {
        onCsv?.();
        return;
      }
      if (!clientId) {
        toast.error('Cannot export: no client context');
        return;
      }
      setLoading(true);
      setError(null);
      const view = option === 'client-pptx' ? 'client' : 'full';
      const options: PortfolioExportOptions = {
        portfolio_id: portfolioId,
        language,
        view,
        regenerate: false,
        preview: false,
        session_id: sessionId,
        client_name: clientName,
      };
      try {
        const created = (await portfolioApiService.exportPortfolioPptx(clientId, options)) as
          | ExportResponse
          | GenerationInFlightResponse;

        // TASK-877: a response is not a deck. Generation runs synchronously inside the
        // POST and a measured run took 82s, so the button routinely got back either a
        // 202 "already generating" (no `export_id`, no file) or a row still `pending`.
        // The old code ran `if (response.pptx_url) download(...)` and then toasted
        // "Portfolio deck ready" UNCONDITIONALLY — the advisor was told the deck was
        // ready by a turn that produced no deck. Wait for the file, then speak.
        const pendingId = isGenerationInFlight(created)
          ? created.in_flight_export_id
          : created.export_id;
        let response: ExportResponse | null = isGenerationInFlight(created) ? null : created;
        if (pendingId && (!response || !response.pptx_url)) {
          abortRef.current = new AbortController();
          response = await waitForExport(pendingId, {
            timeoutMs: POLL_BUDGET_MS,
            signal: abortRef.current.signal,
          });
          // Unmounted mid-wait: the work continues server-side and the deck lands in
          // Files. Saying anything here would be talking to nobody.
          if (!aliveRef.current) return;
        }

        if (!response) {
          // A 202 that named no export to poll. Nothing to wait on and nothing to show.
          toast.error('Export is still generating. Try again in a moment.');
          setError('generating');
          return;
        }
        if (response.pptx_url) {
          triggerDownload(response.pptx_url);
          onExported?.(response);
          toast.success('Portfolio deck ready');
          return;
        }

        onExported?.(response);
        if (response.status === 'failed') {
          const why = response.error_message || 'generation failed';
          toast.error(`Export failed: ${why}`);
          setError(why);
        } else if (isTerminalExportStatus(response.status)) {
          // Finished, and produced no file. `superseded` is the ordinary way here — a
          // newer version replaced this one — and it is NOT "still generating", which is
          // the message an earlier cut gave it. Reporting a settled export as in-flight
          // sends the advisor back to wait for something that will never arrive.
          toast.error(`Export finished without a file (${response.status}).`);
          setError(response.status);
        } else {
          // Still running when the poll budget ran out. True, and not a failure.
          toast.error('Export is still generating. Check Files in a moment.');
          setError('generating');
        }
      } catch (err) {
        if (err instanceof MultiplePortfoliosError) {
          // TODO future-task: render an in-place picker. For now: surface the
          // server-suggested list as a toast and bail.
          const names = err.response.portfolios
            .map((p) => (p as { name?: string }).name || p.id)
            .filter(Boolean)
            .join(', ');
          toast.error(`Multiple portfolios for this client. Specify one: ${names}`);
          setError('multiple portfolios');
        } else if (isClientSideTimeout(err)) {
          // The request gave up; the generation did not. Report what is true.
          toast.error('Export is still generating. Check Files in a moment.');
          setError('generating');
        } else {
          // TASK-898: say WHICH failure. A 402 from the deck generator's AI provider used to
          // arrive as an opaque 500 and toast "Request failed with status code 500", which
          // tells the advisor nothing and reads like their data. The backend now returns a
          // structured detail; prefer it over the transport's own sentence.
          const message = exportFailureMessage(err);
          toast.error(message);
          setError(message);
        }
      } finally {
        setLoading(false);
      }
    },
    [clientId, portfolioId, sessionId, clientName, onExported, onCsv]
  );

  return { loading, error, runExport };
}
